import { Box, Button, Typography } from '@mui/material';
import { t } from 'i18next';
import React from 'react';
import { formatNumberWithComma } from '../../../../utils/utils';
import AurIcon, { IconNames } from '../../../common/aur-icon/AurIcon';
import {
    PAGE_NAME,
    usePermissionsByPage,
} from '../../../../hooks/permissions/usePermissionsByPage';
import { IWorkflowMetricsProps } from './IWorkflowMetricsProps';

const ConsilioWorkflowMetrics: React.FC<IWorkflowMetricsProps> = ({
    projectDetails,
    reportMetrics,
    onDataDeliveriesClick,
    onDatasetsProcessingClick,
    onProcessedDatasetsClick,
    onTotalVolumesMappedClick,
}) => {
    const {
        canViewInkateReports,
        canViewProgressReports,
        canViewProcessingSummaryReports,
        canViewCompletedMappedDataReports,
    } = usePermissionsByPage(
        PAGE_NAME.PROJECT_OVERVIEW,
        projectDetails.projectId
    );

    return (
        <>
            {/* Data Deliveries */}
            <Box
                className={`set1 tile ${!canViewInkateReports ? `no-hover` : ''}`}
                onClick={() =>
                    canViewInkateReports && onDataDeliveriesClick?.()
                }
            >
                <Box>
                    <Typography
                        className="typo-text label1_size mute-text"
                        tabIndex={0}
                    >
                        {t('welcomeScreenSingleClient.mediaAndEvidence')}
                    </Typography>
                    <Typography
                        className="typo-text display3_size"
                        tabIndex={0}
                    >
                        {formatNumberWithComma(
                            reportMetrics?.mediaAndEvidenceSets
                        ) || '0'}
                    </Typography>
                </Box>
                {canViewInkateReports && (
                    <Button
                        className="button-icon button-exit buttonSmall_Size"
                        aria-label={t(
                            'welcomeScreenSingleClient.clickMediaEvidenceButton'
                        )}
                        onClick={onDataDeliveriesClick}
                    >
                        {t('welcomeScreenSingleClient.clickMediaEvidenceButton')}
                        &nbsp;
                        <AurIcon icon={IconNames.ChevronRight} />
                    </Button>
                )}
            </Box>

            {/* Datasets Processing */}
            <Box
                className={`set1 tile ${!canViewProgressReports ? `no-hover` : ''}`}
                onClick={() =>
                    canViewProgressReports &&
                    onDatasetsProcessingClick?.()
                }
            >
                <Box>
                    <Typography
                        className="typo-text label1_size mute-text"
                        tabIndex={0}
                    >
                        {t('welcomeScreenSingleClient.datasetsProcessing')}
                    </Typography>
                    <div className="data-process">
                        <Typography
                            className="typo-text display3_size"
                            tabIndex={0}
                        >
                            {formatNumberWithComma(
                                reportMetrics?.datasetsProcessing
                            ) || '0'}
                        </Typography>
                        <Typography
                            className="typo-text display3_size"
                            tabIndex={0}
                        >
                            *
                        </Typography>
                    </div>
                </Box>
                {canViewProgressReports && (
                    <Button
                        className="button-icon button-exit buttonSmall_Size"
                        aria-label={t(
                            'welcomeScreenSingleClient.clickDatasetProcessingButton'
                        )}
                        onClick={onDatasetsProcessingClick}
                    >
                        {t('welcomeScreenSingleClient.clickDatasetProcessingButton')}
                        &nbsp;
                        <AurIcon icon={IconNames.ChevronRight} />
                    </Button>
                )}
            </Box>

            {/* Processed Datasets */}
            <Box
                className={`set1 tile ${!canViewProcessingSummaryReports ? `no-hover` : ''}`}
                onClick={() =>
                    canViewProcessingSummaryReports &&
                    onProcessedDatasetsClick?.()
                }
            >
                <Box>
                    <Typography
                        className="typo-text label1_size mute-text"
                        tabIndex={0}
                    >
                        {t('welcomeScreenSingleClient.processedDatasets')}
                    </Typography>
                    <Typography
                        className="typo-text display3_size"
                        tabIndex={0}
                    >
                        {formatNumberWithComma(
                            reportMetrics?.datasetsProcessed
                        ) || '0'}
                    </Typography>
                </Box>
                {canViewProcessingSummaryReports && (
                    <Button
                        className="button-icon button-exit buttonSmall_Size"
                        aria-label={t(
                            'welcomeScreenSingleClient.clickProcessedDatasetsButton'
                        )}
                        onClick={onProcessedDatasetsClick}
                    >
                        {t('welcomeScreenSingleClient.clickProcessedDatasetsButton')}
                        <AurIcon icon={IconNames.ChevronRight} />
                    </Button>
                )}
            </Box>

            {/* Total Volumes Mapped */}
            <Box
                className={`set1 tile ${!canViewCompletedMappedDataReports ? `no-hover` : ''}`}
                style={{ borderRight: 0, paddingRight: 32 }}
                onClick={() =>
                    canViewCompletedMappedDataReports &&
                    onTotalVolumesMappedClick?.()
                }
            >
                <Box>
                    <Typography
                        className="typo-text label1_size mute-text"
                        tabIndex={0}
                    >
                        {t('welcomeScreenSingleClient.totalVolumesMapped')}
                    </Typography>
                    <div className="text-inline">
                        <Typography
                            className="typo-text display3_size"
                            tabIndex={0}
                        >
                            {reportMetrics?.totalVolumesMapped ?? 0}
                        </Typography>
                    </div>
                </Box>

                {canViewCompletedMappedDataReports && (
                    <Button
                        className="button-icon button-exit buttonSmall_Size"
                        aria-label={t(
                            'welcomeScreenSingleClient.clickVolumesMappedButton'
                        )}
                        onClick={onTotalVolumesMappedClick}
                    >
                        {t('welcomeScreenSingleClient.clickVolumesMappedButton')}
                        <AurIcon icon={IconNames.ChevronRight} />
                    </Button>
                )}
            </Box>
        </>
    );
};

export default ConsilioWorkflowMetrics;
